import { useState, useEffect, useCallback, useRef } from 'react';
import * as XLSX from 'xlsx';
import useAuthStore from '../store/authStore';
import api from '../api/offlineClient';
import pushService from '../services/pushService';
import { uploadAvatar } from '../utils/avatarUpload';

const ROLES_ADMIN = ['admin', 'jefe', 'administrativo'];
function fmtTime(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
}

async function downloadDoc(doc) {
  try {
    const res = await api.get(`/api/documents/${doc.id}/file`, { responseType: 'blob' });
    const url = URL.createObjectURL(res);
    const a = document.createElement('a');
    a.href = url;
    a.download = doc.file_name || `nota-gastos-sem${doc.semana}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    alert('Archivo no disponible');
  }
}

async function viewPhoto(doc) {
  try {
    const blob = await api.get(`/api/documents/${doc.id}/file`, { responseType: 'blob' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch {
    alert('Foto no disponible');
  }
}

function compressImage(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const MAX = 1200;
        let w = img.width, h = img.height;
        if (w > MAX || h > MAX) {
          if (w > h) { h = Math.round(h * MAX / w); w = MAX; }
          else { w = Math.round(w * MAX / h); h = MAX; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        const b64 = canvas.toDataURL('image/jpeg', 0.82).split(',')[1];
        resolve({ b64, name: file.name });
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function MiPerfil() {
  const user = useAuthStore(s => s.user);
  const updateUser = useAuthStore(s => s.updateUser);
  const isAdmin = ROLES_ADMIN.includes(user?.rol);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const avatarInputRef = useRef();

  async function handleAvatarFile(e) {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setAvatarUploading(true);
    try {
      const { foto_url, foto_public_id } = await uploadAvatar(file);
      await api.patch('/api/auth/me/foto', { foto_url, foto_public_id });
      updateUser({ foto_url });
    } catch (err) {
      alert('No se pudo subir la foto: ' + err.message);
    } finally {
      setAvatarUploading(false);
    }
  }

  const [emp,      setEmp]      = useState(null);
  const [docs,     setDocs]     = useState([]);
  const [stats,    setStats]    = useState(null);
  const [facturas, setFacturas] = useState([]);
  const [abonos,   setAbonos]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [pushPerm,   setPushPerm]   = useState(() => 'Notification' in window ? Notification.permission : 'unsupported');
  const [pushBusy,   setPushBusy]   = useState(false);
  const [pushActive, setPushActive] = useState(false);

  const [auditLog,     setAuditLog]     = useState([]);
  const [auditOpen,    setAuditOpen]    = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);

  // Modal nueva factura
  const [modalFact,  setModalFact]  = useState(false);
  const [fDesc,      setFDesc]      = useState('');
  const [fImporte,   setFImporte]   = useState('');
  const [fFecha,     setFFecha]     = useState('');
  const [fFile,      setFFile]      = useState(null);
  const [savingFact, setSavingFact] = useState(false);
  const fileRef = useRef();
  const [nominas,    setNominas]    = useState([]);
  const [pwOpen,     setPwOpen]     = useState(false);
  const [pwForm,     setPwForm]     = useState({ current: '', next: '', confirm: '' });
  const [pwSaving,   setPwSaving]   = useState(false);
  const [pwMsg,      setPwMsg]      = useState(null);
  const [sec,        setSec]        = useState({});
  const tog = key => setSec(p => ({ ...p, [key]: !p[key] }));
  const [editNota, setEditNota] = useState(null);

  function handleNotaSaved(updated) {
    setDocs(prev => prev.map(d => d.id === updated.id ? { ...d, ...updated } : d));
    setEditNota(null);
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [empRes, docsRes, statsRes, factRes, nomRes] = await Promise.all([
        api.get('/api/employees/me').catch(() => ({ data: null })),
        api.get('/api/documents/mine').catch(() => ({ data: [] })),
        api.get('/api/documents/mine/stats').catch(() => ({ data: null })),
        api.get('/api/facturas/mine').catch(() => ({ data: [] })),
        api.get('/api/nominas/mine').catch(() => ({ data: [] })),
      ]);
      setEmp(empRes.data);
      setDocs(docsRes.data || []);
      setStats(statsRes.data);
      setFacturas(factRes.data || []);
      setNominas(nomRes.data || []);
      if (empRes.data?.id) {
        api.get(`/api/employees/${empRes.data.id}/abonos`)
          .then(r => setAbonos(r.data || []))
          .catch(() => {});
      }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    navigator.serviceWorker.ready
      .then(reg => reg.pushManager.getSubscription())
      .then(sub => { if (sub && Notification.permission === 'granted') setPushActive(true); })
      .catch(() => {});
  }, []);

  async function deleteDoc(doc) {
    if (!window.confirm(`¿Eliminar "${doc.nombre || 'este documento'}"?`)) return;
    try {
      await api.delete(`/api/documents/${doc.id}`);
      setDocs(prev => prev.filter(d => d.id !== doc.id));
    } catch { alert('No se pudo eliminar'); }
  }

  async function handleFactFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    const compressed = await compressImage(file);
    setFFile(compressed);
    e.target.value = '';
  }

  async function submitFactura() {
    if (!fDesc || !fImporte) return;
    setSavingFact(true);
    try {
      const res = await api.post('/api/facturas/create', {
        descripcion: fDesc,
        importe: parseFloat(fImporte),
        fecha: fFecha || undefined,
        file_b64: fFile?.b64 || null,
        file_name: fFile?.name || null,
      });
      setFacturas(prev => [res.data, ...prev]);
      setModalFact(false);
      setFDesc(''); setFImporte(''); setFFecha(''); setFFile(null);
    } catch (e) { alert(e.message); }
    finally { setSavingFact(false); }
  }

  async function downloadNomina(n) {
    try {
      const blob = await api.get(`/api/nominas/${n.id}/file`, { responseType: 'blob' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = n.file_name || `nomina-${n.mes}-${n.año}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      setNominas(prev => prev.map(x => x.id === n.id ? { ...x, leida: true } : x));
    } catch { alert('No se pudo descargar'); }
  }

  async function loadAudit() {
    setAuditLoading(true);
    try {
      const res = await api.get('/api/fichajes/audit');
      setAuditLog(res.data || []);
    } catch { setAuditLog([]); }
    setAuditLoading(false);
  }

  async function changePassword() {
    if (!pwForm.current || !pwForm.next || !pwForm.confirm) return;
    if (pwForm.next !== pwForm.confirm) { setPwMsg({ ok: false, text: 'Las contraseñas nuevas no coinciden' }); return; }
    if (pwForm.next.length < 6) { setPwMsg({ ok: false, text: 'Mínimo 6 caracteres' }); return; }
    setPwSaving(true); setPwMsg(null);
    try {
      await api.post('/api/auth/change-password', { currentPassword: pwForm.current, newPassword: pwForm.next });
      setPwMsg({ ok: true, text: 'Contraseña actualizada correctamente' });
      setPwForm({ current: '', next: '', confirm: '' });
    } catch (e) {
      setPwMsg({ ok: false, text: e.response?.data?.error || e.message });
    } finally { setPwSaving(false); }
  }

  if (loading) return <div style={{ padding: 32, color: 'var(--text-dim)', textAlign: 'center' }}>Cargando…</div>;

  const notas    = docs.filter(d => d.tipo === 'nota_gastos');
  const fotosDocs = docs.filter(d => d.tipo === 'factura');

  const totalNotasGastos  = parseFloat(stats?.total_gastos_empresa || 0);
  const totalAprobadas    = facturas.filter(f => f.estado === 'aprobada').reduce((s, f) => s + parseFloat(f.importe || 0), 0);
  const saldoInicial      = parseFloat(emp?.saldo_inicial || 0);
  const totalAbonos       = abonos.reduce((s, a) => s + parseFloat(a.importe || 0), 0);
  const cartera           = saldoInicial + totalAbonos - totalNotasGastos - totalAprobadas;

  const movements = [
    ...abonos.map(ab => ({
      id: 'ab_' + ab.id,
      tipo: parseFloat(ab.importe) >= 0 ? 'entrada' : 'salida',
      tag: 'Abono',
      concepto: ab.concepto || 'Abono',
      importe: Math.abs(parseFloat(ab.importe)),
      fecha: ab.fecha || ab.created_at,
    })),
    ...notas.filter(d => parseFloat(d.total_gastos) > 0).map(d => ({
      id: 'ng_' + d.id,
      tipo: 'salida',
      tag: 'Nota gastos',
      concepto: d.nombre || `Nota gastos · Sem ${d.semana}/${d.año}`,
      importe: parseFloat(d.total_gastos),
      fecha: d.created_at,
    })),
    ...facturas.filter(f => f.estado === 'aprobada').map(f => ({
      id: 'fa_' + f.id,
      tipo: 'salida',
      tag: 'Reembolso',
      concepto: f.descripcion || 'Reembolso',
      importe: parseFloat(f.importe),
      fecha: f.fecha || f.created_at,
    })),
  ].sort((a, b) => {
    if (!a.fecha && !b.fecha) return 0;
    if (!a.fecha) return 1;
    if (!b.fecha) return -1;
    return new Date(b.fecha) - new Date(a.fecha);
  });

  return (
    <div style={{ padding: 'clamp(12px,3vw,24px)', maxWidth: 860, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
        <div
          onClick={() => !avatarUploading && avatarInputRef.current?.click()}
          title="Cambiar foto de perfil"
          style={{
            position: 'relative', width: 60, height: 60, borderRadius: '50%',
            background: 'var(--amber)', color: '#000', fontSize: 22, fontWeight: 900,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            overflow: 'hidden', cursor: avatarUploading ? 'wait' : 'pointer', flexShrink: 0,
            border: '2px solid var(--border)',
          }}>
          {avatarUploading ? (
            <span style={{ fontSize: 11 }}>…</span>
          ) : user?.foto_url ? (
            <img src={user.foto_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            (user?.nombre || '?').charAt(0).toUpperCase()
          )}
          <div style={{
            position: 'absolute', bottom: -1, right: -1, width: 20, height: 20, borderRadius: '50%',
            background: 'var(--surface2)', border: '1px solid var(--border)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10,
          }}>📷</div>
        </div>
        <input ref={avatarInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarFile} />
        <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text)' }}>Mi Perfil</div>
      </div>

      {/* Datos del empleado */}
      <div style={{ ...CARD, marginBottom: 12 }}>
        <div style={STITLE}>Datos del empleado</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: '8px 20px', marginTop: 14 }}>
          <DataRow icon="👤" label="Nombre"        val={emp?.nombre || user?.nombre} />
          <DataRow icon="🔢" label="Nº empleado"   val={emp?.numero_empleado ?? '—'} />
          <DataRow icon="🆔" label="DNI"           val={emp?.dni ?? '—'} />
          <DataRow icon="📱" label="Tel. personal" val={emp?.telefono ?? '—'} />
          <DataRow icon="☎️" label="Tel. empresa"  val={emp?.telefono_empresa ?? '—'} />
          <DataRow icon="✉️" label="Email"         val={emp?.email || user?.email} />
          <DataRow icon="📍" label="Dirección"     val={emp?.direccion ?? '—'} />
          <DataRow icon="🏷️" label="Estado"        val={emp?.estado ?? '—'} />
        </div>
      </div>

      {/* Cartera */}
      <div style={{ ...CARD, marginBottom: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: 12, marginBottom: 12 }}>
          <StatCard icon="💼" label="Cartera Segavan"
            val={`${cartera.toFixed(2)} €`}
            color={cartera >= 0 ? '#4ade80' : '#f87171'}
            sub={saldoInicial > 0 ? `Saldo inicial: ${saldoInicial.toFixed(2)} €` : null}
            onClick={() => tog('abonos')}
            active={sec.abonos} />
          <StatCard icon="📄" label="Notas de gastos"
            val={stats ? stats.total_notas : '—'} color="#60a5fa"
            onClick={() => tog('notas')}
            active={sec.notas} />
        </div>
        {movements.length > 0 && (
          <>
            {sec.abonos && (
              <div style={{ display: 'grid', gap: 4, marginTop: 4 }}>
                {movements.map(m => (
                  <div key={m.id} style={{ background: 'var(--surface-deep)', borderRadius: 6, padding: '7px 10px', border: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, minWidth: 0 }}>
                      <span style={{ fontSize: 13, color: m.tipo === 'entrada' ? '#4ade80' : '#f87171', fontWeight: 800, flexShrink: 0 }}>
                        {m.tipo === 'entrada' ? '+' : '−'}{m.importe.toFixed(2)} €
                      </span>
                      <span style={{ fontSize: 11, color: 'var(--text-dim)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                        {m.fecha ? new Date(m.fecha).toLocaleDateString('es-ES') : '—'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3, minWidth: 0 }}>
                      <span style={{ fontSize: 10, color: 'var(--text-dim)', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 4, padding: '1px 5px', whiteSpace: 'nowrap', flexShrink: 0 }}>{m.tag}</span>
                      <span style={{ fontSize: 11, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.concepto}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        {fotosDocs.length > 0 && (
          <>
            <button onClick={() => tog('fotos')} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', padding: '6px 0', width: '100%', borderTop: '1px solid var(--border)', paddingTop: 10, marginTop: movements.length > 0 ? 6 : 0 }}>
              <span style={{ fontSize: 10, color: 'var(--text-dim)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em' }}>Fotos tickets y facturas</span>
              <span style={{ fontSize: 11, color: 'var(--text-dim)', transform: sec.fotos ? 'rotate(180deg)' : 'rotate(0deg)', display: 'inline-block', transition: 'transform .2s' }}>▼</span>
            </button>
            {sec.fotos && (
              <div style={{ display: 'grid', gap: 6, marginTop: 4 }}>
                {fotosDocs.slice(0, 30).map(d => (
                  <div key={d.id} style={DOC_ROW}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{d.nombre || 'Factura'}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>
                        {new Date(d.created_at).toLocaleDateString('es-ES', { day:'2-digit', month:'short', year:'numeric' })}
                        {d.semana && <span> · Sem {d.semana}/{d.año}</span>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                      {parseFloat(d.total_gastos) > 0 && <div style={{ fontSize: 14, fontWeight: 800, color: '#4ade80', whiteSpace: 'nowrap' }}>{parseFloat(d.total_gastos).toFixed(2)} €</div>}
                      {d.has_file && <button onClick={() => viewPhoto(d)} title="Ver foto" style={BTN_VIEW}>🖼</button>}
                      <button onClick={() => deleteDoc(d)} title="Eliminar" style={BTN_DEL}>🗑</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>


      {/* Notas de gastos */}
      {sec.notas && (
        <div style={{ padding: '0 16px 16px' }}>
          <style>{`@keyframes mpSpin { to { transform: rotate(360deg); } }`}</style>
          {notas.length === 0 ? (
            <div style={{ color: 'var(--text-dim)', fontSize: 12, padding: '10px 0' }}>Sin registros. Se guardarán automáticamente al generar el Excel.</div>
          ) : (
            <div style={{ display: 'grid', gap: 6 }}>
              {notas.map(d => (
                <MiNotaRow key={d.id} d={d} onDelete={deleteDoc}
                  onEdit={(d.parte_data && !d.presentado_at) ? () => setEditNota(d) : null}
                  onPresented={(id, to) => setDocs(prev => prev.map(x => x.id === id ? { ...x, presentado_at: new Date().toISOString(), presentado_a: to } : x))} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Nóminas */}
      <SectionCard title="📜 Mis nóminas" open={sec.nominas} onToggle={() => tog('nominas')}>
        {nominas.length === 0 ? (
          <div style={{ color: 'var(--text-dim)', fontSize: 12 }}>Sin nóminas disponibles. Aparecerán aquí cuando sean subidas por administración.</div>
        ) : (
          <div style={{ display: 'grid', gap: 6 }}>
            {nominas.map(n => (
              <div key={n.id} style={DOC_ROW}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>
                    {['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'][(n.mes||1)-1]} {n.año}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>
                    {n.leida ? <span style={{ color: '#4ade80' }}>Descargada ✓</span> : <span style={{ color: '#fbbf24' }}>● Nueva nómina disponible</span>}
                  </div>
                </div>
                <button onClick={() => downloadNomina(n)} style={BTN_DL} title="Descargar PDF">⬇ PDF</button>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {/* Cambiar contraseña */}
      <SectionCard title="Cambiar contraseña" open={pwOpen} onToggle={() => { setPwOpen(o => !o); setPwMsg(null); }}>
        <div style={{ display: 'grid', gap: 10, maxWidth: 380 }}>
          <Fld label="Contraseña actual">
            <input type="password" value={pwForm.current} onChange={e => setPwForm(f => ({ ...f, current: e.target.value }))} placeholder="••••••••" style={INP} autoComplete="current-password" />
          </Fld>
          <Fld label="Nueva contraseña">
            <input type="password" value={pwForm.next} onChange={e => setPwForm(f => ({ ...f, next: e.target.value }))} placeholder="Mínimo 6 caracteres" style={INP} autoComplete="new-password" />
          </Fld>
          <Fld label="Confirmar nueva contraseña">
            <input type="password" value={pwForm.confirm} onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))} placeholder="Repite la contraseña" style={INP} autoComplete="new-password" />
          </Fld>
          {pwMsg && (
            <div style={{ fontSize: 12, fontWeight: 700, padding: '7px 10px', borderRadius: 6,
              background: pwMsg.ok ? 'rgba(74,222,128,.12)' : 'rgba(248,113,113,.12)',
              color: pwMsg.ok ? '#4ade80' : '#f87171',
              border: `1px solid ${pwMsg.ok ? 'rgba(74,222,128,.4)' : 'rgba(248,113,113,.4)'}` }}>
              {pwMsg.text}
            </div>
          )}
          <button onClick={changePassword} disabled={pwSaving || !pwForm.current || !pwForm.next || !pwForm.confirm}
            style={{ background: 'var(--amber)', color: '#000', border: 'none', padding: '9px 0', borderRadius: 7,
              cursor: 'pointer', fontWeight: 800, fontSize: 13,
              opacity: (!pwForm.current || !pwForm.next || !pwForm.confirm) ? .5 : 1 }}>
            {pwSaving ? 'Guardando…' : 'Cambiar contraseña'}
          </button>
        </div>
      </SectionCard>

      {/* Notificaciones push */}
      {pushPerm !== 'unsupported' && (
        <div style={{ ...CARD, marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ ...STITLE, marginBottom: 6 }}>🔔 Notificaciones</div>
              <div style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 4 }}>
                {pushPerm === 'denied'
                  ? 'Bloqueadas en el navegador — ve a Configuración del sitio para permitirlas.'
                  : pushActive
                  ? 'Recibirás avisos de nuevas nóminas, actualizaciones de reembolsos y vacaciones.'
                  : 'Actívalas para recibir avisos sin abrir la app.'}
              </div>
            </div>
            {pushPerm !== 'denied' && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: pushBusy ? 'wait' : 'pointer', flexShrink: 0 }}>
                <div style={{ position: 'relative', width: 44, height: 24 }}>
                  <input
                    type="checkbox"
                    checked={pushActive}
                    disabled={pushBusy}
                    onChange={async e => {
                      setPushBusy(true);
                      if (e.target.checked) {
                        const ok = await pushService.requestAndSubscribe();
                        setPushPerm(Notification.permission);
                        setPushActive(ok && Notification.permission === 'granted');
                        if (!ok && Notification.permission !== 'denied') alert('No se pudo activar. Prueba de nuevo.');
                      } else {
                        await pushService.unsubscribe();
                        setPushActive(false);
                      }
                      setPushBusy(false);
                    }}
                    style={{ opacity: 0, position: 'absolute', inset: 0, cursor: 'inherit', margin: 0, width: '100%', height: '100%' }}
                  />
                  <div style={{ position: 'absolute', inset: 0, borderRadius: 12, background: pushActive ? 'var(--amber)' : 'var(--surface2)', border: '1px solid var(--border)', transition: 'background .2s' }} />
                  <div style={{ position: 'absolute', top: 3, left: pushActive ? 23 : 3, width: 18, height: 18, background: pushActive ? '#000' : 'var(--text-dim)', borderRadius: '50%', transition: 'left .2s' }} />
                </div>
                <span style={{ fontSize: 12, fontWeight: 700, color: pushActive ? 'var(--text)' : 'var(--text-dim)', minWidth: 84 }}>
                  {pushBusy ? '…' : pushActive ? 'Activadas' : 'Desactivadas'}
                </span>
              </label>
            )}
          </div>
        </div>
      )}

      {/* Registro de ediciones — solo admin */}
      {isAdmin && (
        <SectionCard title="Registro de ediciones de fichaje" open={auditOpen}
          onToggle={() => { setAuditOpen(o => { if (!o) loadAudit(); return !o; }); }}>
          {auditLoading ? (
            <div style={{ color: 'var(--text-dim)', fontSize: 12 }}>Cargando…</div>
          ) : auditLog.length === 0 ? (
            <div style={{ color: 'var(--text-dim)', fontSize: 12 }}>Sin ediciones registradas aún.</div>
          ) : (
            <div style={{ display: 'grid', gap: 6 }}>
              {auditLog.map(a => (
                <div key={a.id} style={DOC_ROW}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)' }}>{a.user_nombre} · {a.fecha?.slice(0,10)}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>
                      Entrada: <span style={{ color: '#f87171' }}>{fmtTime(a.old_entrada)}</span>{' → '}<span style={{ color: '#4ade80' }}>{fmtTime(a.new_entrada)}</span>
                      &emsp;Salida: <span style={{ color: '#f87171' }}>{fmtTime(a.old_salida)}</span>{' → '}<span style={{ color: '#4ade80' }}>{fmtTime(a.new_salida)}</span>
                    </div>
                    <div style={{ fontSize: 10, color: '#4e5a7a', marginTop: 3 }}>
                      editado por <strong style={{ color: 'var(--text-dim)' }}>{a.admin_nombre}</strong>{' · '}
                      {new Date(a.changed_at).toLocaleString('es-ES', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      )}

      {/* Modal nueva solicitud de reembolso */}
      {modalFact && (
        <div style={{ position: 'fixed', inset: 0, background: '#000a', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 }}
          onClick={e => e.target === e.currentTarget && setModalFact(false)}>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 22, width: '90%', maxWidth: 400 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--amber)', marginBottom: 16 }}>Nueva solicitud de reembolso</div>

            <Fld label="Descripción">
              <input type="text" value={fDesc} onChange={e => setFDesc(e.target.value)}
                placeholder="Ej: Material de obra, hotel, etc." style={INP} />
            </Fld>
            <Fld label="Importe (€)">
              <input type="number" value={fImporte} onChange={e => setFImporte(e.target.value)}
                placeholder="0.00" min="0" step="0.01" style={INP} />
            </Fld>
            <Fld label="Fecha">
              <input type="date" value={fFecha} onChange={e => setFFecha(e.target.value)} style={INP} />
            </Fld>
            <Fld label="Justificante (opcional)">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button onClick={() => fileRef.current?.click()}
                  style={{ background: 'rgba(96,165,250,.1)', border: '1px solid rgba(96,165,250,.3)', color: '#60a5fa', padding: '7px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                  {fFile ? '✓ ' + fFile.name.slice(0, 20) : '📎 Adjuntar foto'}
                </button>
                {fFile && (
                  <button onClick={() => setFFile(null)}
                    style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: 16 }}>✕</button>
                )}
                <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFactFile} />
              </div>
            </Fld>

            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button onClick={submitFactura} disabled={savingFact || !fDesc || !fImporte}
                style={{ flex: 1, background: 'var(--amber)', color: '#000', border: 'none', padding: '9px 0', borderRadius: 7, cursor: 'pointer', fontWeight: 800, fontSize: 13, opacity: (!fDesc || !fImporte) ? .5 : 1 }}>
                {savingFact ? 'Enviando…' : 'Enviar solicitud'}
              </button>
              <button onClick={() => setModalFact(false)}
                style={{ background: 'var(--surface2)', color: 'var(--text-dim)', border: 'none', padding: '9px 14px', borderRadius: 7, cursor: 'pointer', fontSize: 12 }}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {editNota && (
        <NotaEditorModal doc={editNota} onClose={() => setEditNota(null)} onSaved={handleNotaSaved} />
      )}
    </div>
  );
}

// ── MiNotaRow: nota de gastos expandible con tabla Excel ──────────────────────

const NG_COLS = [
  { idx: 0,  label: 'Fecha',       align: 'left',   w: 70  },
  { idx: 1,  label: 'Instalación', align: 'left',   w: null },
  { idx: 2,  label: 'Nº',          align: 'center', w: 46  },
  { idx: 3,  label: 'M.O.',        align: 'center', w: 46  },
  { idx: 4,  label: 'Desp.',       align: 'center', w: 46  },
  { idx: 7,  label: '½ Dieta',     align: 'center', w: 54  },
  { idx: 8,  label: 'Dieta',       align: 'center', w: 46  },
  { idx: 9,  label: 'Hotel',       align: 'right',  w: 62  },
  { idx: 10, label: 'Vehículo',    align: 'right',  w: 62  },
  { idx: 11, label: 'Material',    align: 'right',  w: 62  },
];
const NG_BADGE = {
  'FESTIVO':    { bg: 'rgba(96,165,250,.12)',  color: '#60a5fa',  border: 'rgba(96,165,250,.3)'  },
  'VACACIONES': { bg: 'rgba(192,132,252,.12)', color: '#c084fc',  border: 'rgba(192,132,252,.3)' },
  'HORAS COMP.':{ bg: 'rgba(52,211,153,.12)',  color: '#34d399',  border: 'rgba(52,211,153,.3)'  },
};

function SendIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}

function MiNotaRow({ d, onDelete, onEdit, onPresented }) {
  const [open,        setOpen]        = useState(false);
  const [fetching,    setFetching]    = useState(false);
  const [tableRows,   setTableRows]   = useState(null);
  const [horasExtras, setHorasExtras] = useState(0);
  const [pwModal,     setPwModal]     = useState(false);
  const [pw,          setPw]          = useState('');
  const [presenting,  setPresenting]  = useState(false);
  const [sendErr,     setSendErr]     = useState(null);
  const gastos = parseFloat(d.total_gastos) || 0;

  async function presentar() {
    if (!pw) return;
    setPresenting(true);
    setSendErr(null);
    try {
      const r = await api.post(`/api/documents/${d.id}/presentar`, { email_password: pw });
      onPresented?.(d.id, r.data?.to);
      setPwModal(false);
      setPw('');
    } catch (e) {
      setSendErr(e.message);
    } finally {
      setPresenting(false);
    }
  }

  function presentarClick() {
    const sem = d.semana ? ` de la semana ${d.semana}` : '';
    if (!window.confirm(`¿Presentar esta nota de gastos${sem}? Se enviará por correo y no se podrá editar después.`)) return;
    setPwModal(true);
  }

  async function toggle() {
    if (open) { setOpen(false); return; }
    if (tableRows !== null) { setOpen(true); return; }
    if (!d.has_file) { setTableRows([]); setOpen(true); return; }
    setFetching(true);
    try {
      const blob = await api.get(`/api/documents/${d.id}/file`, { responseType: 'blob' });
      const ab   = await blob.arrayBuffer();
      const wb   = XLSX.read(new Uint8Array(ab), { type: 'array' });
      const ws   = wb.Sheets[wb.SheetNames[0]];
      const raw  = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null });
      const DATE_PAT = /^\d{1,2}[-/]/;
      const SPECIAL  = new Set(['FESTIVO', 'VACACIONES', 'HORAS COMP.']);
      const data = raw.filter(r => {
        const a = String(r?.[0] ?? '');
        const b = String(r?.[1] ?? '').toUpperCase().trim();
        return DATE_PAT.test(a) || (a && SPECIAL.has(b));
      });
      const extrasCell  = raw[36]?.[0];
      const extrasMatch = typeof extrasCell === 'string' ? extrasCell.match(/(\d+[,.]\d*|\d+)\s*HORAS EXTRAS/i) : null;
      setTableRows(data);
      setHorasExtras(extrasMatch ? parseFloat(extrasMatch[1].replace(',', '.')) : 0);
    } catch { setTableRows([]); }
    setFetching(false);
    setOpen(true);
  }

  const totals = tableRows?.length ? (() => {
    const acc = {};
    [3, 4, 9, 10, 11].forEach(idx => { acc[idx] = tableRows.reduce((s, r) => s + (parseFloat(r?.[idx] ?? 0) || 0), 0); });
    acc[7] = tableRows.reduce((s, r) => s + (parseFloat(r?.[7] ?? 0) > 0 ? 1 : 0), 0);
    acc[8] = tableRows.reduce((s, r) => s + (parseFloat(r?.[8] ?? 0) > 0 ? 1 : 0), 0);
    return acc;
  })() : null;

  return (
    <div style={{ background: 'var(--surface-deep)', border: `1px solid ${open ? 'rgba(245,158,11,.45)' : 'var(--border)'}`, borderRadius: 8, overflow: 'hidden', transition: 'border-color .18s' }}>
      <div onClick={toggle} style={{ padding: '10px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, cursor: 'pointer', userSelect: 'none' }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {d.semana ? `SEMANA ${d.semana}` : (d.nombre || 'Nota de Gastos')}
            </span>
            {d.nota_numero && (
              <span style={{ fontSize: 9, fontWeight: 800, padding: '1px 6px', borderRadius: 3, background: 'rgba(139,92,246,.12)', color: '#a78bfa', border: '1px solid rgba(139,92,246,.3)' }}>
                Nº {d.nota_numero}
              </span>
            )}
            {d.presentado_at && (
              <span title={d.presentado_a ? `Enviada a ${d.presentado_a}` : ''} style={{ fontSize: 9, fontWeight: 800, padding: '1px 6px', borderRadius: 3, background: 'rgba(74,222,128,.12)', color: '#4ade80', border: '1px solid rgba(74,222,128,.3)' }}>
                ✔ Presentada
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 3, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span>{new Date(d.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
            {gastos > 0 && <span style={{ color: 'var(--amber)', fontWeight: 700 }}>{gastos.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span>}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0 }}>
          {d.has_file && (
            <button onClick={e => { e.stopPropagation(); presentarClick(); }} title="Presentar por correo"
              style={{ background: 'rgba(96,165,250,.12)', border: '1px solid rgba(96,165,250,.4)', color: '#60a5fa', borderRadius: 6, padding: '4px 9px', cursor: 'pointer', fontSize: 12, fontWeight: 700, lineHeight: 1, display: 'flex', alignItems: 'center', gap: 4 }}>
              <SendIcon /> Enviar
            </button>
          )}
          {d.has_file && (
            <button onClick={e => { e.stopPropagation(); downloadDoc(d); }} title="Descargar Excel"
              style={{ background: 'rgba(74,222,128,.12)', border: '1px solid rgba(74,222,128,.4)', color: '#4ade80', borderRadius: 6, padding: '4px 7px', cursor: 'pointer', fontSize: 13, lineHeight: 1 }}>
              ⬇
            </button>
          )}
          <button onClick={e => { e.stopPropagation(); onDelete(d); }} title="Eliminar" style={BTN_DEL}>🗑</button>
          {onEdit && (
            <button onClick={e => { e.stopPropagation(); onEdit(); }} title="Editar"
              style={{ background: 'rgba(245,158,11,.12)', border: '1px solid rgba(245,158,11,.4)', color: 'var(--amber)', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 12, fontWeight: 700, lineHeight: 1 }}>
              ✎
            </button>
          )}
          <span style={{ fontSize: 18, color: open ? 'var(--amber)' : 'var(--text-dim)', display: 'inline-block', transition: 'transform .2s, color .2s', transform: open ? 'rotate(90deg)' : 'rotate(0deg)', lineHeight: 1 }}>›</span>
        </div>
      </div>

      {open && (
        <div style={{ borderTop: '1px solid var(--border)', background: 'rgba(0,0,0,.18)' }}>
          {fetching ? (
            <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text-dim)', fontSize: 12 }}>
              <span style={{ display: 'inline-block', width: 16, height: 16, border: '2px solid var(--border)', borderTopColor: 'var(--amber)', borderRadius: '50%', animation: 'mpSpin .7s linear infinite', verticalAlign: 'middle', marginRight: 7 }} />
              Cargando datos…
            </div>
          ) : !tableRows?.length ? (
            <div style={{ padding: '20px 14px', textAlign: 'center', color: 'var(--text-dim)', fontSize: 12 }}>
              {d.has_file ? 'El archivo no contiene filas de datos.' : 'No hay archivo adjunto para esta nota.'}
            </div>
          ) : (
            <div style={{ overflowX: 'auto', padding: '0 0 2px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 620 }}>
                <thead>
                  <tr style={{ background: 'rgba(245,158,11,.06)' }}>
                    {NG_COLS.map(c => (
                      <th key={c.idx} style={{ padding: '7px 10px', textAlign: c.align, width: c.w || undefined, color: 'var(--amber)', fontSize: 10, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', borderBottom: '1px solid rgba(245,158,11,.2)', whiteSpace: 'nowrap' }}>
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((row, i) => {
                    const tipo    = String(row[1] || '').toUpperCase();
                    const badge   = NG_BADGE[tipo];
                    const isSpcl  = !!badge;
                    return (
                      <tr key={i} style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,.025)' }}>
                        {NG_COLS.map(c => {
                          const raw = row?.[c.idx];
                          const val = (raw === '' || raw === 0 || raw === null || raw === undefined) ? null : raw;
                          if (c.idx === 1 && isSpcl) return (
                            <td key={c.idx} colSpan={NG_COLS.length - 1} style={{ padding: '6px 10px', borderBottom: '1px solid rgba(255,255,255,.04)' }}>
                              <span style={{ display: 'inline-block', padding: '1px 8px', borderRadius: 4, fontSize: 10, fontWeight: 800, letterSpacing: '.06em', background: badge.bg, color: badge.color, border: `1px solid ${badge.border}` }}>{tipo}</span>
                            </td>
                          );
                          if (isSpcl && c.idx !== 0) return null;
                          if ((c.idx === 7 || c.idx === 8) && val) {
                            return <td key={c.idx} style={{ padding: '6px 10px', textAlign: c.align, borderBottom: '1px solid rgba(255,255,255,.04)', color: c.idx === 7 ? '#fbbf24' : '#4ade80', fontWeight: 900, fontSize: 13 }}>✓</td>;
                          }
                          const fmt = (c.idx >= 9 && val !== null)
                            ? Number(val).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'
                            : val !== null ? String(val) : '—';
                          return (
                            <td key={c.idx} style={{ padding: '6px 10px', textAlign: c.align, color: val === null ? 'var(--text-dim)' : 'var(--text)', borderBottom: '1px solid rgba(255,255,255,.04)', whiteSpace: 'nowrap' }}>
                              {fmt}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
                {totals && (
                  <tfoot>
                    <tr style={{ background: 'rgba(245,158,11,.06)', borderTop: '2px solid rgba(245,158,11,.25)' }}>
                      {NG_COLS.map(c => {
                        if (c.idx === 0) return <td key={c.idx} style={{ padding: '7px 10px', fontSize: 10, fontWeight: 800, color: 'var(--amber)', textTransform: 'uppercase', letterSpacing: '.06em', whiteSpace: 'nowrap' }}>Totales</td>;
                        if (c.idx === 1 || c.idx === 2) return <td key={c.idx} />;
                        const sum = totals[c.idx];
                        if (sum === undefined || sum === null) return <td key={c.idx} />;
                        if (c.idx === 7 || c.idx === 8) return <td key={c.idx} style={{ padding: '7px 10px', textAlign: c.align, fontWeight: 800, color: sum > 0 ? (c.idx === 7 ? '#fbbf24' : '#4ade80') : 'var(--text-dim)', whiteSpace: 'nowrap' }}>{sum > 0 ? `${sum} día${sum !== 1 ? 's' : ''}` : '—'}</td>;
                        if (c.idx === 3 || c.idx === 4) return <td key={c.idx} style={{ padding: '7px 10px', textAlign: c.align, fontWeight: 800, color: sum > 0 ? 'var(--amber)' : 'var(--text-dim)', whiteSpace: 'nowrap' }}>{sum > 0 ? `${sum.toLocaleString('es-ES', { maximumFractionDigits: 1 })} h` : '—'}</td>;
                        return <td key={c.idx} style={{ padding: '7px 10px', textAlign: c.align, fontWeight: 800, color: sum > 0 ? 'var(--amber)' : 'var(--text-dim)', whiteSpace: 'nowrap' }}>{sum > 0 ? `${sum.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '—'}</td>;
                      })}
                    </tr>
                    {horasExtras > 0 && (
                      <tr style={{ background: 'rgba(251,191,36,.05)' }}>
                        <td colSpan={NG_COLS.length} style={{ padding: '5px 10px', fontSize: 11, color: '#fbbf24', fontWeight: 700 }}>
                          ⏱ Horas extras semana: {horasExtras.toLocaleString('es-ES', { maximumFractionDigits: 1 })} h
                        </td>
                      </tr>
                    )}
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </div>
      )}

      {pwModal && (
        <div onClick={e => e.target === e.currentTarget && !presenting && setPwModal(false)}
          style={{ position: 'fixed', inset: 0, background: '#000a', zIndex: 9500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, width: '100%', maxWidth: 360 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--amber)', marginBottom: 4 }}>Presentar nota de gastos</div>
            <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 14 }}>
              Se enviará por correo desde tu propia cuenta de empresa. Introduce tu contraseña de correo (no se guarda).
            </div>
            <input
              type="password"
              value={pw}
              onChange={e => setPw(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && presentar()}
              placeholder="Contraseña de tu correo"
              autoFocus
              style={{ background: 'var(--surface-deep)', border: '1.5px solid var(--border)', borderRadius: 6, color: 'var(--text)', padding: '8px 10px', width: '100%', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' }}
            />
            {sendErr && <div style={{ fontSize: 11, color: '#f87171', marginTop: 8 }}>{sendErr}</div>}
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button onClick={() => { setPwModal(false); setPw(''); setSendErr(null); }} disabled={presenting}
                style={{ flex: 1, padding: '9px 0', background: 'var(--surface2)', color: 'var(--text-dim)', border: 'none', borderRadius: 7, cursor: 'pointer', fontWeight: 700, fontSize: 12 }}>
                Cancelar
              </button>
              <button onClick={presentar} disabled={presenting || !pw}
                style={{ flex: 2, padding: '9px 0', background: '#60a5fa', color: '#000', border: 'none', borderRadius: 7, cursor: 'pointer', fontWeight: 800, fontSize: 13, opacity: (presenting || !pw) ? .6 : 1 }}>
                {presenting ? 'Enviando…' : '📤 Enviar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── NotaEditorModal: reabre nota-gastos.html precargado para corregir una nota ─

function NotaEditorModal({ doc, onClose, onSaved }) {
  const [status,  setStatus]  = useState('Cargando formulario…');
  const [saving,  setSaving]  = useState(false);
  const iframeRef = useRef(null);

  useEffect(() => {
    function onMsg(e) {
      if (!e.data || e.data.type !== 'sgv_doc_update' || e.data.docId !== doc.id) return;
      (async () => {
        setSaving(true);
        setStatus('Guardando…');
        try {
          const r = await api.put(`/api/documents/${doc.id}/nota`, {
            nombre: e.data.nombre || null,
            año: e.data.año || null,
            semana: e.data.semana || null,
            total_gastos: e.data.totalGastos || 0,
            nota_numero: e.data.notaNum || null,
            parte_data: e.data.notaData || null,
          });
          if (e.data.fileB64) {
            await api.patch(`/api/documents/${doc.id}/file`, { file_b64: e.data.fileB64, file_name: e.data.fileName || doc.file_name });
          }
          onSaved({ id: doc.id, ...r.data, has_file: e.data.fileB64 ? true : doc.has_file });
        } catch (err) {
          setStatus('Error al guardar: ' + err.message);
          setSaving(false);
        }
      })();
    }
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [doc, onSaved]);

  function handleLoad() {
    iframeRef.current?.contentWindow?.postMessage({
      type: 'sgv_load_nota', docId: doc.id, notaData: doc.parte_data || {},
    }, '*');
    setStatus(null);
  }

  return (
    <div onClick={e => e.target === e.currentTarget && !saving && onClose()}
      style={{ position: 'fixed', inset: 0, background: '#000c', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
      <div style={{ background: '#0b0d15', borderRadius: 12, width: '100%', maxWidth: 980, height: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', border: '1px solid #1c2035' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: '1px solid #1c2035', flexShrink: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--amber)' }}>Editar nota de gastos</div>
          <button onClick={() => !saving && onClose()} style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: 18 }}>✕</button>
        </div>
        {status && (
          <div style={{ padding: '8px 16px', fontSize: 12, color: saving ? '#4ade80' : 'var(--text-dim)', flexShrink: 0 }}>{status}</div>
        )}
        <iframe
          ref={iframeRef}
          src="/nota-gastos.html"
          title="Editar nota de gastos"
          onLoad={handleLoad}
          style={{ flex: 1, border: 'none', width: '100%' }}
        />
      </div>
    </div>
  );
}

function SectionCard({ title, open, onToggle, children, extra }) {
  return (
    <div style={{ ...CARD, marginBottom: 12 }}>
      <button onClick={onToggle} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
        <div style={{ ...STITLE, marginBottom: 0 }}>{title}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {extra}
          <span style={{ fontSize: 12, color: 'var(--text-dim)', transition: 'transform .2s', display: 'inline-block', transform: open ? 'rotate(180deg)' : 'rotate(0deg)', flexShrink: 0 }}>▼</span>
        </div>
      </button>
      {open && <div style={{ marginTop: 14 }}>{children}</div>}
    </div>
  );
}

function DataRow({ icon, label, val }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, padding: '4px 0', minWidth: 0 }}>
      <span style={{ fontSize: 13, flexShrink: 0 }}>{icon}</span>
      <span style={{ fontSize: 10, color: 'var(--text-dim)', minWidth: 80, flexShrink: 0, textTransform: 'uppercase', fontWeight: 700 }}>{label}</span>
      <span style={{ fontSize: 12, color: 'var(--text)', fontWeight: 600, minWidth: 0, overflowWrap: 'break-word' }}>{val}</span>
    </div>
  );
}

function StatCard({ icon, label, val, color, sub, onClick, active }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: active ? 'rgba(245,158,11,.06)' : 'var(--surface)',
        border: `1px solid ${active ? 'rgba(245,158,11,.35)' : 'var(--border)'}`,
        borderRadius: 10, padding: '14px 16px',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'border-color .15s, background .15s',
        userSelect: 'none',
      }}
    >
      <div style={{ fontSize: 22, marginBottom: 6 }}>{icon}</div>
      <div style={{ fontSize: 22, fontWeight: 900, color: color || 'var(--text)' }}>{val}</div>
      {sub && <div style={{ fontSize: 9, color: 'var(--text-dim)', marginTop: 2 }}>{sub}</div>}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
        <div style={{ fontSize: 10, color: 'var(--text-dim)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em' }}>{label}</div>
        {onClick && <span style={{ fontSize: 10, color: 'var(--text-dim)', transform: active ? 'rotate(180deg)' : 'rotate(0deg)', display: 'inline-block', transition: 'transform .2s' }}>▼</span>}
      </div>
    </div>
  );
}

function Fld({ label, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ fontSize: 10, color: 'var(--text-dim)', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  );
}

const CARD    = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px', marginBottom: 0 };
const STITLE  = { fontSize: 10, color: 'var(--amber)', fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 0 };
const DOC_ROW = { background: 'var(--surface-deep)', border: '1px solid var(--border)', borderRadius: 7, padding: '10px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 };
const BTN_DL  = { background: 'rgba(74,222,128,.12)', border: '1px solid rgba(74,222,128,.4)', color: '#4ade80', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 14, lineHeight: 1, flexShrink: 0 };
const BTN_VIEW= { background: 'rgba(96,165,250,.1)', border: '1px solid rgba(96,165,250,.3)', color: '#60a5fa', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 14, lineHeight: 1 };
const BTN_DEL = { background: 'rgba(248,113,113,.12)', border: '1px solid rgba(248,113,113,.4)', color: '#f87171', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 13, lineHeight: 1, flexShrink: 0 };
const INP     = { background: 'var(--surface-deep)', border: '1.5px solid var(--border)', borderRadius: 6, color: 'var(--text)', padding: '8px 10px', width: '100%', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' };
